import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import "./AlertFormEdit.css";
import { useIntl } from "react-intl";
import { Toast } from "../../../helpers/alert";
import { useDispatch, useSelector } from "react-redux";
import { getAllcompanies } from "../../../redux/apiCalls/companiesCall/companiesCall";

const FREQUENCY_ORDER = ["daily", "weekly", "monthly", "realtime"];

const InvoicePenaltyAlertFormEdit = ({
  users,
  onCancel,
  onSave,
  initialData,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const userToken = useSelector((state) => state.user.currentUser?.accessToken);
  const companies = useSelector((state) => state.companies.companies);

  useEffect(() => {
    getAllcompanies(dispatch, userToken, "company");
  }, [dispatch, userToken]);

  const availableConcepts = useMemo(
    () => [
      {
        value: "Potencia Excedida",
        label: "Potencia Excedida",
        description: "Alerta si consumo o costo son mayor a 0",
      },
      {
        value: "Coseno fi",
        label: "Coseno fi",
        description: "Alerta si el valor es menor al configurado",
        hasThreshold: true,
        defaultThreshold: 0.95,
      },
      {
        value: "Tangente fi",
        label: "Tangente fi",
        description: "Alerta si el valor es mayor al configurado",
        hasThreshold: true,
        defaultThreshold: 0.32868,
      },
    ],
    [],
  );

  const getActiveFrequencyKey = useCallback((frequency = {}) => {
    return (
      FREQUENCY_ORDER.find((key) => frequency?.[key]?.enabled) || "daily"
    );
  }, []);

  const getFrequencyEmails = useCallback((frequency = {}, key = "daily") => {
    const emails = frequency?.[key]?.emails;

    return Array.isArray(emails) ? emails : [];
  }, []);

  const getAvailableUsersForCompany = useCallback(
    (companyId) => {
      if (!Array.isArray(users)) return [];

      return users
        .filter(
          (user) => String(user.id_company) === String(companyId),
        )
        .filter((user) => user.email)
        .map((user) => ({
          value: user.id,
          label: user.email,
          data: user,
        }));
    },
    [users],
  );

  const companyOptions = useMemo(() => {
    if (!Array.isArray(companies)) return [];

    return companies.map((company) => ({
      value: company.id,
      label: company.name || `Empresa ${company.id}`,
      data: company,
    }));
  }, [companies]);

  const getConceptName = (concept) => {
    if (typeof concept === "string") return concept;
    return concept?.name || "";
  };

  const getConceptThreshold = (concept, conceptName) => {
    if (concept?.threshold !== undefined && concept?.threshold !== null) {
      return concept.threshold;
    }

    if (conceptName === "Coseno fi") return 0.95;
    if (conceptName === "Tangente fi") return 0.32868;

    return "";
  };

  const toCompanyOption = useCallback(
    (companyId) => {
      if (!companyId) return null;

      return (
        companyOptions.find(
          (company) => String(company.value) === String(companyId),
        ) || null
      );
    },
    [companyOptions],
  );

  const toConceptOptions = useCallback(
    (concepts = []) => {
      if (!Array.isArray(concepts)) return [];

      return concepts
        .map((concept) => {
          const conceptName = getConceptName(concept);

          return availableConcepts.find(
            (option) => option.value === conceptName,
          );
        })
        .filter(Boolean);
    },
    [availableConcepts],
  );

  const buildThresholdsFromConcepts = useCallback((concepts = []) => {
    const thresholds = {
      "Coseno fi": 0.95,
      "Tangente fi": 0.32868,
    };

    if (!Array.isArray(concepts)) return thresholds;

    concepts.forEach((concept) => {
      const conceptName = getConceptName(concept);

      if (conceptName === "Coseno fi") {
        thresholds["Coseno fi"] = getConceptThreshold(concept, conceptName);
      }

      if (conceptName === "Tangente fi") {
        thresholds["Tangente fi"] = getConceptThreshold(concept, conceptName);
      }
    });

    return thresholds;
  }, []);

  const toEmailOptions = useCallback(
    (values = [], companyId) => {
      if (!Array.isArray(users)) return [];

      const filteredUsers = users.filter(
        (user) => String(user.id_company) === String(companyId),
      );

      const byId = new Map(
        filteredUsers.map((user) => [Number(user.id), user]),
      );

      const byEmail = new Map(
        filteredUsers.map((user) => [
          String(user.email || "").toLowerCase(),
          user,
        ]),
      );

      return values
        .map((value) => {
          const str = String(value ?? "");
          const isNumeric = typeof value === "number" || /^[0-9]+$/.test(str);

          if (isNumeric) {
            const user = byId.get(Number(value));
            return user?.email
              ? { value: user.id, label: user.email, data: user }
              : null;
          }

          const email = str.trim().toLowerCase();
          const user = byEmail.get(email);

          if (user) {
            return { value: user.id, label: user.email, data: user };
          }

          return email ? { value: email, label: String(value) } : null;
        })
        .filter(Boolean);
    },
    [users],
  );

  const toUserOptions = useCallback(
    (values = [], companyId) => {
      if (!Array.isArray(users)) return [];

      const filteredUsers = users.filter(
        (user) => String(user.id_company) === String(companyId),
      );

      return values
        .map((value) => {
          const user = filteredUsers.find(
            (u) =>
              String(u.id || u.id_user) === String(value) ||
              String(u.email || u.mail) === String(value),
          );

          if (!user) return null;

          return {
            value: user.id || user.id_user,
            label:
              user.email ||
              user.mail ||
              user.name ||
              user.username ||
              `Usuario ${user.id || user.id_user}`,
            data: user,
          };
        })
        .filter(Boolean);
    },
    [users],
  );

  const buildStateFromInitial = useCallback(() => {
    const config = initialData?.config || {};
    const frequency = config.frequency || {};
    const companyId = config.company;
    const concepts = config.concepts || [];
    const visualNotifications = config.visualNotifications || {};
    const activeFrequencyKey = getActiveFrequencyKey(frequency);
    const frequencyEmails = getFrequencyEmails(frequency, activeFrequencyKey);
    const selectedRecipients = toEmailOptions(frequencyEmails, companyId);
    const availableUsersForCompany = getAvailableUsersForCompany(companyId);

    if (Number(initialData?.id) === 50) {
      console.log(`[PENALIZACION EDIT][50] frequency =`, frequency);
      console.log(
        `[PENALIZACION EDIT][50] activeFrequency =`,
        activeFrequencyKey,
      );
      console.log(
        `[PENALIZACION EDIT][50] frequencyEmails =`,
        frequencyEmails,
      );
      console.log(
        `[PENALIZACION EDIT][50] selectedRecipients =`,
        selectedRecipients.map((option) => option.value),
      );
      console.log(
        `[PENALIZACION EDIT][50] availableUsers =`,
        availableUsersForCompany.map((option) => option.value),
      );
    }

    return {
      name: initialData?.name || "",
      active: initialData?.active === 1 || initialData?.active === true,
      company: toCompanyOption(companyId),
      concepts: toConceptOptions(concepts),
      conceptThresholds: buildThresholdsFromConcepts(concepts),
      emails: selectedRecipients,
      frequencyType: activeFrequencyKey === "weekly" ? "weekly" : "daily",
      visualNotificationsEnabled: visualNotifications.enabled === true,
      visualNotificationsUsers: toUserOptions(
        visualNotifications.users || [],
        companyId,
      ),
    };
  }, [
    initialData,
    toCompanyOption,
    toConceptOptions,
    buildThresholdsFromConcepts,
    toEmailOptions,
    toUserOptions,
    getActiveFrequencyKey,
    getFrequencyEmails,
    getAvailableUsersForCompany,
  ]);

  const [formData, setFormData] = useState(() => buildStateFromInitial());
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const lastInitKeyRef = useRef(null);

  const initKey = useMemo(
    () =>
      initialData?.id ??
      JSON.stringify({
        name: initialData?.name,
        active: initialData?.active,
        company: initialData?.config?.company,
        concepts: initialData?.config?.concepts || [],
        activeFrequency: getActiveFrequencyKey(
          initialData?.config?.frequency || {},
        ),
        emails: getFrequencyEmails(
          initialData?.config?.frequency || {},
          getActiveFrequencyKey(initialData?.config?.frequency || {}),
        ),
        visualNotifications: initialData?.config?.visualNotifications || {},
      }),
    [initialData, getActiveFrequencyKey, getFrequencyEmails],
  );

  useEffect(() => {
    if (!initialData || !companies?.length || !users?.length) return;

    const sameInit = lastInitKeyRef.current === initKey;
    if (isDirty && sameInit) return;

    setFormData(buildStateFromInitial());
    lastInitKeyRef.current = initKey;
    setIsDirty(false);
  }, [initialData, companies, users, initKey, buildStateFromInitial, isDirty]);

  const userEmailOptions = useMemo(() => {
    if (!formData.company || !Array.isArray(users)) return [];

    return users
      .filter(
        (user) => String(user.id_company) === String(formData.company.value),
      )
      .filter((user) => user.email)
      .map((user) => ({
        value: user.id,
        label: user.email,
        data: user,
      }));
  }, [users, formData.company]);

  const visualNotificationUserOptions = useMemo(() => {
    if (!formData.company || !Array.isArray(users)) return [];

    return users
      .filter(
        (user) => String(user.id_company) === String(formData.company.value),
      )
      .map((user) => ({
        value: user.id || user.id_user,
        label:
          user.email ||
          user.mail ||
          user.name ||
          user.username ||
          `Usuario ${user.id || user.id_user}`,
        data: user,
      }))
      .filter((option) => option.value);
  }, [users, formData.company]);

  const handleChange = (field, value) => {
    setIsDirty(true);

    setFormData((prev) => {
      if (field === "company") {
        return {
          ...prev,
          company: value,
          emails: [],
          visualNotificationsUsers: [],
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleThresholdChange = (conceptName, value) => {
    setIsDirty(true);

    setFormData((prev) => ({
      ...prev,
      conceptThresholds: {
        ...prev.conceptThresholds,
        [conceptName]: value,
      },
    }));
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }

    if (!formData.company) {
      newErrors.company = "Selecciona una empresa";
    }

    if (!formData.concepts?.length) {
      newErrors.concepts = "Selecciona al menos un concepto";
    }

    const selectedConceptNames = formData.concepts.map((c) => c.value);

    if (selectedConceptNames.includes("Coseno fi")) {
      const threshold = Number(formData.conceptThresholds["Coseno fi"]);

      if (Number.isNaN(threshold) || threshold <= 0) {
        newErrors.cosenoFi = "El umbral de Coseno fi debe ser válido";
      }
    }

    if (selectedConceptNames.includes("Tangente fi")) {
      const threshold = Number(formData.conceptThresholds["Tangente fi"]);

      if (Number.isNaN(threshold) || threshold <= 0) {
        newErrors.tangenteFi = "El umbral de Tangente fi debe ser válido";
      }
    }

    if (!formData.emails?.length) {
      newErrors.emails = "Selecciona al menos un usuario destinatario";
    }

    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
  }, [formData]);

  useEffect(() => {
    validateForm();
  }, [formData, validateForm]);

  const buildConceptPayload = () => {
    return formData.concepts.map((concept) => {
      if (concept.value === "Coseno fi") {
        return {
          name: "Coseno fi",
          threshold: Number(formData.conceptThresholds["Coseno fi"]),
        };
      }

      if (concept.value === "Tangente fi") {
        return {
          name: "Tangente fi",
          threshold: Number(formData.conceptThresholds["Tangente fi"]),
        };
      }

      return {
        name: concept.value,
      };
    });
  };

  const handleSubmit = () => {
    Swal.fire({
      title: "¿Desea guardar los cambios de la alerta?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.formatMessage({
        id: "confirm.yes",
        defaultMessage: "Sí, continuar.",
      }),
      cancelButtonText: intl.formatMessage({
        id: "confirm.no",
        defaultMessage: "Cancelar",
      }),
    }).then((result) => {
      if (result.isConfirmed && isValid) {
        const payload = {
          type: "PENALIZACION",
          name: formData.name?.toUpperCase(),
          active: formData.active ? 1 : 0,
          config: {
            company: formData.company.value,
            company_name: formData.company.label,
            concepts: buildConceptPayload(),
            frequency: {
              daily: {
                enabled: formData.frequencyType === "daily",
                time: formData.frequencyType === "daily" ? "09:00" : null,
                emails:
                  formData.frequencyType === "daily"
                    ? formData.emails.map((email) => email.value)
                    : [],
              },
              weekly: {
                enabled: formData.frequencyType === "weekly",
                day: formData.frequencyType === "weekly" ? "monday" : null,
                time: formData.frequencyType === "weekly" ? "09:00" : null,
                emails:
                  formData.frequencyType === "weekly"
                    ? formData.emails.map((email) => email.value)
                    : [],
                phones: [],
              },
              realtime: {
                enabled: false,
                emails: [],
                phones: [],
              },
              monthly: {
                enabled: false,
                day: null,
                time: null,
                emails: [],
                phones: [],
              },
            },
            visualNotifications: {
              enabled: formData.visualNotificationsEnabled,
              users: formData.visualNotificationsEnabled
                ? formData.visualNotificationsUsers.map((user) => user.value)
                : [],
            },
          },
        };

        if (Number(initialData?.id) === 50) {
          console.log(
            `[PENALIZACION EDIT][50] payload.frequency =`,
            payload.config.frequency,
          );
          console.log(
            `[PENALIZACION EDIT][50] payload.visualNotifications =`,
            payload.config.visualNotifications,
          );
        }

        onSave(payload);
      } else if (!isValid) {
        Toast.fire({
          icon: "info",
          title: intl.formatMessage(
            {
              id: "error.saveAlert",
              defaultMessage: "No se pudo guardar la alerta: {errors}",
            },
            { errors: Object.values(errors).join(", ") },
          ),
        });
      }
    });
  };

  return (
    <div className="coste-form">
      <h3 className="coste-title">Editar alerta: Penalizaciones en facturas</h3>

      <label className="coste-label">Nombre de la alerta</label>
      <input
        type="text"
        className="coste-input"
        value={formData.name}
        onChange={(e) => handleChange("name", e.target.value)}
        placeholder="Ej: Penalizaciones en facturas"
      />
      {errors.name && <small className="error-text">{errors.name}</small>}

      <label className="coste-label">¿Activar?</label>
      <div className="coste-inline">
        <label>
          <input
            type="radio"
            checked={!!formData.active}
            onChange={() => handleChange("active", true)}
          />{" "}
          Sí
        </label>

        <label>
          <input
            type="radio"
            checked={!formData.active}
            onChange={() => handleChange("active", false)}
          />{" "}
          No
        </label>
      </div>

      <label className="coste-label">Empresa</label>
      <Select
        options={companyOptions}
        value={formData.company}
        onChange={(val) => handleChange("company", val)}
        className="coste-select"
        placeholder="Selecciona una empresa"
      />
      {errors.company && <small className="error-text">{errors.company}</small>}

      <label className="coste-label">Conceptos a monitorear</label>
      <Select
        isMulti
        options={availableConcepts}
        value={formData.concepts}
        onChange={(val) => handleChange("concepts", val || [])}
        className="coste-select"
        placeholder="Selecciona conceptos"
        formatOptionLabel={(option) => (
          <div>
            <div>{option.label}</div>
            {option.description && (
              <div style={{ fontSize: "0.8em", color: "#666" }}>
                {option.description}
              </div>
            )}
          </div>
        )}
      />
      {errors.concepts && (
        <small className="error-text">{errors.concepts}</small>
      )}

      {formData.concepts.some((c) => c.value === "Coseno fi") && (
        <div style={{ marginTop: 12 }}>
          <label className="coste-label">Umbral Coseno fi</label>
          <input
            type="number"
            step="0.00001"
            className="coste-input"
            value={formData.conceptThresholds["Coseno fi"]}
            onChange={(e) => handleThresholdChange("Coseno fi", e.target.value)}
          />
          {errors.cosenoFi && (
            <small className="error-text">{errors.cosenoFi}</small>
          )}
        </div>
      )}

      {formData.concepts.some((c) => c.value === "Tangente fi") && (
        <div style={{ marginTop: 12 }}>
          <label className="coste-label">Umbral Tangente fi</label>
          <input
            type="number"
            step="0.00001"
            className="coste-input"
            value={formData.conceptThresholds["Tangente fi"]}
            onChange={(e) =>
              handleThresholdChange("Tangente fi", e.target.value)
            }
          />
          {errors.tangenteFi && (
            <small className="error-text">{errors.tangenteFi}</small>
          )}
        </div>
      )}

      <label className="coste-label">Usuarios destinatarios</label>
      <Select
        isMulti
        options={userEmailOptions}
        value={formData.emails}
        onChange={(val) => handleChange("emails", val || [])}
        className="coste-select"
        isDisabled={!formData.company}
        placeholder={
          formData.company
            ? "Selecciona usuarios"
            : "Primero selecciona una empresa"
        }
      />
      {errors.emails && <small className="error-text">{errors.emails}</small>}

      <label className="coste-label">Frecuencia de aviso</label>

      <Select
        options={[
          { value: "daily", label: "Diario" },
          {
            value: "weekly",
            label: "Semanal (lunes 09:00)",
          },
        ]}
        value={[
          { value: "daily", label: "Diario" },
          {
            value: "weekly",
            label: "Semanal (lunes 09:00)",
          },
        ].find((option) => option.value === formData.frequencyType)}
        onChange={(val) => handleChange("frequencyType", val.value)}
        className="coste-select"
      />

      <div style={{ marginTop: 12, color: "#666", fontSize: 13 }}>
        {formData.frequencyType === "daily"
          ? "La alerta se enviará todos los días a las 09:00 AM."
          : "La alerta se enviará todos los lunes a las 09:00 AM."}
      </div>

      <label className="coste-label">Mostrar banner in-app</label>

      <div className="coste-inline">
        <label>
          <input
            type="radio"
            checked={formData.visualNotificationsEnabled}
            onChange={() => handleChange("visualNotificationsEnabled", true)}
          />{" "}
          Sí
        </label>

        <label>
          <input
            type="radio"
            checked={!formData.visualNotificationsEnabled}
            onChange={() => handleChange("visualNotificationsEnabled", false)}
          />{" "}
          No
        </label>
      </div>

      {formData.visualNotificationsEnabled && (
        <>
          <label className="coste-label">Usuarios que verán el banner</label>

          <Select
            isMulti
            options={visualNotificationUserOptions}
            value={formData.visualNotificationsUsers}
            onChange={(val) =>
              handleChange("visualNotificationsUsers", val || [])
            }
            className="coste-select"
            isDisabled={!formData.company}
            placeholder={
              formData.company
                ? "Selecciona usuarios para el banner"
                : "Primero selecciona una empresa"
            }
          />

          {errors.visualNotificationsUsers && (
            <small className="error-text">
              {errors.visualNotificationsUsers}
            </small>
          )}

          <div style={{ marginTop: 8, color: "#666", fontSize: 13 }}>
            Estos usuarios verán el banner al ingresar al dashboard cuando se
            detecten nuevas penalizaciones.
          </div>
        </>
      )}

      <div className="coste-actions">
        <button
          className="coste-btn-save"
          onClick={handleSubmit}
          disabled={!isValid}
        >
          Guardar
        </button>

        <button className="coste-btn-cancel" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default InvoicePenaltyAlertFormEdit;
